import type { Dispatch, SetStateAction } from 'react';
import { decimal, label, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import type {
  useImportKspMutation,
  useUploadKspOriginalMutation,
} from '@shared/api/endpoints/ksp';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { Button } from '@shared/ui/Button';
import { Card, Field, Input, Notice, Row, Select, Small } from '../styles';
import { choice, sourceText, textField } from '../types';
import { PagedTable } from '../components/CollectionTable';
import { ImportCard } from '../components/ImportCard';
import { ProjectSettingsCard } from '../components/ProjectSettingsCard';

export interface SourcesTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  timezone: string;
  editable: boolean;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  fail: (error: unknown) => void;
  setMessage: (msg: string) => void;
  setErrors: Dispatch<SetStateAction<string[]>>;
  token?: string | null;
  file: File | null;
  setFile: (file: File | null) => void;
  importKind: string;
  setImportKind: (kind: string) => void;
  dateFormat: string;
  setDateFormat: (fmt: string) => void;
  separator: string;
  setSeparator: (sep: string) => void;
  upload: ReturnType<typeof useImportKspMutation>[0];
  uploading: { isLoading: boolean };
  uploadOriginal: ReturnType<typeof useUploadKspOriginalMutation>[0];
  uploadingOriginal: { isLoading: boolean };
  detailedImportId: string;
  setDetailedImportId: (id: string) => void;
  loadingImport: boolean;
  importError: unknown;
  workOptions: { value: string; label: string }[];
  calendarOptions: { value: string; label: string }[];
  fieldSites: Array<{ id: string; name: string }>;
  fieldSiteOptions: { value: string; label: string }[];
  fieldSitesError?: boolean;
  mtoRunOptions: { value: string; label: string }[];
  mtoRowOptions: { value: string; label: string }[];
  latestMtoRunId: string;
}

export function SourcesTab({
  workspace: d,
  projectId,
  versionId,
  timezone,
  editable,
  permission,
  open,
  execute,
  fail,
  setMessage,
  setErrors,
  token,
  file,
  setFile,
  importKind,
  setImportKind,
  dateFormat,
  setDateFormat,
  separator,
  setSeparator,
  upload,
  uploading,
  uploadOriginal,
  uploadingOriginal,
  detailedImportId,
  setDetailedImportId,
  loadingImport,
  importError,
  workOptions,
  calendarOptions,
  fieldSites,
  fieldSiteOptions,
  fieldSitesError,
  mtoRunOptions,
  mtoRowOptions,
  latestMtoRunId,
}: SourcesTabProps) {
  return (
    <>
      <ProjectSettingsCard
        workspace={d}
        projectId={projectId}
        timezone={timezone}
        permission={permission}
        open={open}
        execute={execute}
      />
      <Card>
        <h3>Шаблон обмена и первичные файлы</h3>
        <Row>
          <Button
            $variant="secondary"
            onClick={async () => {
              const result = await downloadAuthorizedFile(
                '/api/v1/agents/ksp/templates/schedule',
                { accessToken: token, fallbackName: 'Шаблон_КСП.csv' },
              );
              if (!result.ok)
                setErrors((prev) => [...prev, `Шаблон недоступен: ${result.error}`]);
            }}
          >
            Скачать шаблон графика
          </Button>
          <Button
            disabled={!permission() || !file || uploadingOriginal.isLoading}
            onClick={async () => {
              if (!file) return;
              try {
                const saved = await uploadOriginal({
                  projectId,
                  file,
                  key: crypto.randomUUID(),
                  expectedVersion: d.version,
                }).unwrap();
                setMessage(
                  `Первичный файл сохранён: ${file.name}. Он доступен для привязки к письму, согласованию и доказательству.`,
                );
                setFile(null);
                if (!saved.id)
                  setErrors((prev) => [...prev, 'Сервер не вернул сохранённый файл.']);
              } catch (error) {
                fail(error);
              }
            }}
          >
            Сохранить выбранный файл как приложение
          </Button>
        </Row>
        <Small>
          Для письма, ЭДО и согласования выберите файл ниже и сохраните его как приложение.
          Разбор строк запускается отдельной кнопкой.
        </Small>
      </Card>
      <Card>
        <h3>Загрузить исходные данные</h3>
        <Row>
          <Field>
            Назначение
            <Select value={importKind} onChange={(e) => setImportKind(e.target.value)}>
              {['schedule', 'rd', 'supply', 'actual', 'period_plan'].map((kind) => (
                <option key={kind} value={kind}>
                  {label(kind)}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Файл XLSX, CSV, DOCX, PDF, JSON
            <Input
              type="file"
              accept=".xlsx,.xlsm,.csv,.docx,.pdf,.json"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <Field>
            Порядок даты
            <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
              <option value="">Только однозначный ISO</option>
              <option value="dmy">День / месяц / год</option>
              <option value="mdy">Месяц / день / год</option>
            </Select>
          </Field>
          <Field>
            Разделитель CSV
            <Select value={separator} onChange={(e) => setSeparator(e.target.value)}>
              <option value=";">Точка с запятой</option>
              <option value=",">Запятая</option>
              <option value="\t">Табуляция</option>
            </Select>
          </Field>
          <Button
            disabled={!permission() || !file || uploading.isLoading}
            onClick={async () => {
              if (!file) return;
              try {
                await upload({
                  projectId,
                  file,
                  kind: importKind,
                  key: crypto.randomUUID(),
                  expectedVersion: d.version,
                  dateFormat,
                  separator,
                }).unwrap();
                setMessage(
                  'Файл принят для разбора. Проверьте строки и сопоставления перед применением.',
                );
                setFile(null);
              } catch (error) {
                fail(error);
              }
            }}
          >
            Загрузить
          </Button>
        </Row>
        <Small>
          Макросы и пересчёт исходной книги не запускаются. Значения и привязки подтверждает
          инженер.
        </Small>
      </Card>
      {d.imports.map((imported) => (
        <ImportCard
          key={imported.id}
          imported={imported}
          token={token}
          timezone={timezone}
          editable={editable}
          versionId={versionId}
          loadingImport={loadingImport}
          detailedImportId={detailedImportId}
          importError={importError}
          workOptions={workOptions}
          calendarOptions={calendarOptions}
          permission={permission}
          open={open}
          execute={execute}
          fail={fail}
          setDetailedImportId={setDetailedImportId}
        />
      ))}
      <Card>
        <h3>РД и количественные привязки</h3>
        {fieldSitesError && (
          <Notice $error>
            Не удалось загрузить площадки / фронты проекта КСП. Обновите данные перед
            привязкой факта Прораба.
          </Notice>
        )}
        <Button
          disabled={!editable || !versionId}
          onClick={() =>
            open(
              'Привязать подтверждённый объём РД',
              [
                choice('activity_id', 'Работа', workOptions),
                {
                  ...choice('field_site_id', 'Площадка / фронт Прораба', fieldSiteOptions),
                  required: false,
                  clears: ['position_id'],
                  help: 'Для выполнения Прораба по титулу выберите точный фронт. Одна позиция и фронт относятся к одной работе графика.',
                },
                {
                  ...textField('position_id', 'Позиция РД выбранного фронта'),
                  type: 'rd-position',
                  projectId,
                },
                textField('quantity', 'Назначенный работе объём'),
                textField('unit', 'Единица'),
                textField(
                  'conversion_factor',
                  'Подтверждённый коэффициент перевода единиц',
                  true,
                  '1',
                ),
                textField('basis', 'Основание и методика распределения'),
                textField(
                  'conversion_basis',
                  'Основание перевода единиц (если различаются)',
                  false,
                ),
              ],
              (values) =>
                execute(`projects/${projectId}/rd-bindings`, {
                  ...values,
                  field_site_id: values.field_site_id || null,
                  version_id: versionId,
                }),
            )
          }
        >
          Назначить объём
        </Button>
        <Button
          disabled={!permission()}
          onClick={async () => {
            try {
              await execute(`projects/${projectId}/source-sync`, {});
            } catch {
              /* journal */
            }
          }}
        >
          Сверить ПТО, Прораба и МТО
        </Button>
        <PagedTable
          rows={d.rd_bindings ?? []}
          columns={[
            {
              name: 'Работа',
              value: (item) => d.activities.find((a) => a.id === item.activity_id)?.code,
            },
            {
              name: 'Ревизия РД / позиция',
              value: (item) => (
                <>
                  {item.record_uid}
                  <Small>{item.original_revision_id}</Small>
                </>
              ),
            },
            {
              name: 'Назначенный объём',
              value: (item) => `${decimal(item.allocated_quantity)} ${item.unit}`,
            },
            {
              name: 'Фронт Прораба',
              value: (item) =>
                item.field_site_id
                  ? (fieldSites.find((s) => s.id === item.field_site_id)?.name ??
                    'Площадка недоступна')
                  : 'Без точной привязки',
            },
            { name: 'Основание', value: (item) => item.basis },
            {
              name: 'Замена источника',
              value: (item) => (
                <Button
                  $size="sm"
                  disabled={!editable || !versionId}
                  onClick={() =>
                    open(
                      'Заменить привязку на новую ревизию РД',
                      [
                        {
                          ...choice(
                            'field_site_id',
                            'Площадка / фронт Прораба',
                            fieldSiteOptions,
                            item.field_site_id ?? '',
                          ),
                          required: false,
                          clears: ['position_id'],
                        },
                        {
                          ...textField(
                            'position_id',
                            'Подтверждённая позиция РД выбранного фронта',
                          ),
                          type: 'rd-position',
                          projectId,
                        },
                        textField(
                          'quantity',
                          'Назначенный работе объём',
                          true,
                          item.allocated_quantity,
                        ),
                        textField(
                          'conversion_factor',
                          'Подтверждённый коэффициент перевода единиц',
                          true,
                          '1',
                        ),
                        textField(
                          'conversion_basis',
                          'Основание перевода единиц (если различаются)',
                          false,
                        ),
                        textField('basis', 'Основание замены источника'),
                      ],
                      (values) =>
                        execute(`projects/${projectId}/rd-bindings`, {
                          ...values,
                          activity_id: item.activity_id,
                          version_id: versionId,
                          replace_binding_id: item.id,
                          field_site_id: values.field_site_id || null,
                        }),
                    )
                  }
                >
                  Заменить ревизию РД
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Ресурсы</h3>
        <Button
          disabled={!editable}
          onClick={() =>
            open(
              'Добавить ресурс',
              [
                textField('name', 'Название'),
                textField('code', 'Код ресурса'),
                choice('calendar_id', 'Календарь ресурса', calendarOptions),
                choice(
                  'kind',
                  'Тип',
                  ['person', 'equipment', 'team'].map((value) => ({
                    value,
                    label: label(value),
                  })),
                ),
                textField('unit', 'Единица мощности'),
                textField('capacity', 'Подтверждённая мощность'),
                textField('basis', 'Основание'),
              ],
              (values) => execute(`projects/${projectId}/resources`, values),
            )
          }
        >
          Добавить
        </Button>
        <PagedTable
          rows={d.resources}
          columns={[
            { name: 'Ресурс', value: (r) => r.name },
            { name: 'Тип', value: (r) => label(r.kind) },
            { name: 'Мощность', value: (r) => `${decimal(r.capacity)} ${r.unit}` },
          ]}
        />
      </Card>
      <Card>
        <h3>Поставки и допущения</h3>
        <Button
          disabled={!editable}
          onClick={() =>
            open(
              'Привязать поставку',
              [
                choice('activity_id', 'Работа', workOptions),
                textField('material_id', 'Материал'),
                textField('required_quantity', 'Потребность'),
                textField('available_quantity', 'Подтверждённое количество', true, '0'),
                textField('unit', 'Единица'),
                choice('mode', 'Подтверждение', [
                  { value: 'confirmed', label: 'Подтверждена источником МТО' },
                  { value: 'assumed_available', label: 'Допущение наличия' },
                ]),
                {
                  ...textField('available_at', 'Дата готовности UTC'),
                  type: 'datetime-local',
                  required: false,
                },
                mtoRowOptions.length > 0
                  ? choice('source_row_id', 'Строка утверждённого прогона МТО', [
                      { value: '', label: '— Выберите строку МТО —' },
                      ...mtoRowOptions,
                    ])
                  : textField('source_row_id', 'Строка утверждённого прогона МТО'),
                mtoRunOptions.length > 0
                  ? choice(
                      'source_run_id',
                      'Прогон МТО',
                      [{ value: '', label: '— Выберите прогон МТО —' }, ...mtoRunOptions],
                      latestMtoRunId,
                    )
                  : textField('source_run_id', 'Прогон МТО'),
                textField('record_uid', 'Устойчивый код позиции'),
                textField('basis', 'Основание'),
              ],
              (values) =>
                execute(`projects/${projectId}/supply-bindings`, {
                  ...values,
                  version_id: versionId,
                  available_at: values.available_at
                    ? new Date(`${values.available_at}:00Z`).toISOString()
                    : null,
                  source_run_id: values.source_run_id || null,
                }),
            )
          }
        >
          Привязать
        </Button>
        <PagedTable
          rows={d.supply_bindings}
          columns={[
            { name: 'Материал', value: (r) => r.material },
            {
              name: 'Работа',
              value: (r) => d.activities.find((a) => a.id === r.activity_id)?.code,
            },
            { name: 'Количество', value: (r) => `${decimal(r.quantity)} ${r.unit}` },
            {
              name: 'Готовность',
              value: (r) => (
                <>
                  {projectDate(r.confirmed_date, timezone)}
                  <Small>
                    {label(r.mode)} · {sourceText(r.source_ref)}
                  </Small>
                </>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
